// Lazy autoplay: video's met [data-lazy-autoplay] krijgen pas een src en
// starten pas wanneer ze (bijna) in de viewport komen. Buiten beeld wordt
// de bron losgekoppeld zodat de download stopt en de verbinding vrijkomt —
// er downloaden dus enkel video's die effectief in beeld staan.
//
// LINGER_MS: een video laadt pas als hij zo lang onafgebroken in beeld is.
// Zo triggert (smooth) voorbijscrollen — bv. de lenis-scroll naar boven na
// een sprig-paginatie — geen downloads van alle tussenliggende cards.
const LINGER_MS = 300

const timers = new WeakMap()

const loaderFor = (video) =>
    video.parentElement?.querySelector('[data-video-loader]')

const observer = new IntersectionObserver(
    (entries) => {
        entries.forEach((entry) => {
            const video = entry.target

            if (entry.isIntersecting) {
                if (video.dataset.lazyLoaded) {
                    // Chrome kan de fetch van een gepauzeerde video opschorten
                    // (blijft op "pending" staan met 0 bytes). Als er nog geen
                    // data binnen is, herstart load() de download.
                    if (video.readyState === HTMLMediaElement.HAVE_NOTHING) {
                        video.load()
                    }
                    video.play().catch(() => {})
                    return
                }

                const timer = setTimeout(() => {
                    if (!video.isConnected) {
                        return
                    }

                    video.dataset.lazyLoaded = 'true'
                    video
                        .querySelectorAll('source[data-src]')
                        .forEach((source) => {
                            source.src = source.dataset.src
                        })
                    video.load()
                    video.play().catch(() => {})
                }, LINGER_MS)

                timers.set(video, timer)
            } else {
                clearTimeout(timers.get(video))

                if (video.dataset.lazyLoaded) {
                    // Hard stoppen i.p.v. enkel pauzeren: src eraf + load()
                    // annuleert de lopende download en geeft de verbinding
                    // vrij. Enkel video's in beeld downloaden dus nog.
                    video.pause()
                    video.querySelectorAll('source[src]').forEach((source) => {
                        source.removeAttribute('src')
                    })
                    video.load()
                    delete video.dataset.lazyLoaded

                    // Spinner terugzetten voor de volgende keer in beeld.
                    loaderFor(video)?.classList.remove('opacity-0')
                }
            }
        })
    },
    { rootMargin: '200px' }
)

const observeVideo = (video) => {
    if (!video.dataset.lazyBound) {
        video.dataset.lazyBound = 'true'
        // Zodra er effectief beeld speelt: spinner-overlay uitfaden.
        video.addEventListener('playing', () => {
            loaderFor(video)?.classList.add('opacity-0')
        })
    }

    observer.observe(video)
}

const observeVideos = (root) => {
    root.querySelectorAll('video[data-lazy-autoplay]').forEach(observeVideo)
}

observeVideos(document)

// Vangt ook content op die later wordt toegevoegd (htmx/Sprig swaps, Alpine, ...)
new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
            if (node.nodeType !== Node.ELEMENT_NODE) {
                return
            }

            if (node.matches('video[data-lazy-autoplay]')) {
                observeVideo(node)
            }

            observeVideos(node)
        })
    })
}).observe(document.body, { childList: true, subtree: true })
